<template>
  <form
    ref="searchForm"
    method="POST"
    :class="[
      $style.container,
      {
        [$style['is-loading']]: isLoading,
      },
    ]"
    @submit.prevent
  >
    <input
      v-model="search"
      type="text"
      :minlength="minLength"
      :placeholder="placeholderText"
      :class="$style.input"
      @input="onInput"
    />
    <img
      v-if="search"
      src="~/assets/icons/close.svg"
      alt="Effacer la recherche"
      :class="$style.clear"
      @click="clearSearch"
    />
    <img
      v-else
      src="~/assets/icons/search.svg"
      alt="Rechercher"
      :class="$style.search"
    />
  </form>
</template>

<script setup lang="ts">
import type { InputSearchProps } from '~/types'
import { InputSearchPropsDefaults } from '~/types'

withDefaults(defineProps<InputSearchProps>(), InputSearchPropsDefaults)
const emit = defineEmits(['update:modelValue'])

const search = ref('')
const searchForm = ref<HTMLInputElement | null>(null)

const clearSearch = () => {
  search.value = ''
}

const onInput = (event: Event) => {
  const { value } = event.target as HTMLInputElement
  emit('update:modelValue', value)
  searchForm.value?.reportValidity()
}
</script>

<style lang="scss" module>
.container {
  position: relative;
  display: flex;
  width: clamp(2.3rem, 100%, 100vw);
  margin-left: auto;
  overflow: hidden;

  @media (min-width: 768px) {
    flex-shrink: 0;
    width: 34.5rem;
  }

  &::before,
  &::after {
    position: absolute;
    bottom: 0;
    height: 1px;
  }

  &::before {
    content: '';
    width: 100%;
    border-bottom: 1px solid rgba(0 0 0 / 0.2);
  }

  &::after {
    left: -25%;
    display: block;
    width: 25%;
    background-color: $blue;
  }

  &:focus-within:not(.is-loading) {
    &::before {
      background-color: $blue;
    }
  }

  &.is-loading {
    &::after {
      content: '';
      animation: loading 0.7s infinite linear;
    }
  }
}

.input {
  width: 100%;
  padding: 1rem 5rem 1rem 0;
  border: 0;

  &:focus {
    outline: 0;
  }
}

.clear,
.search {
  position: absolute;
  top: 50%;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2rem;
  height: 2rem;
  padding: 0.3rem;
  transform: translateY(-50%);
}

.clear {
  cursor: pointer;
}

@keyframes loading {
  0% {
    left: -25%;
  }

  90%,
  100% {
    left: 100%;
  }
}
</style>
